import React, { useState, useEffect, useCallback, useRef } from 'react'
import { toast } from 'react-hot-toast'
import { fetchStrings } from '../js/data.js'

// Types for the hook
interface StringCache {
   data: Record<string, string>
   language: string
   hostUrl?: string // Store the host URL in the cache
}

interface LocalizationOptions {
   showErrors?: boolean
}

interface LocalizationResult {
   status: number
   data?: Record<string, string>
   error?: string
}

interface UseLocalizationReturn {
   strings: Record<string, string>
   getString: (key: string, fallback?: string) => string
   getFormattedString: (
      key: string,
      variables?: Record<string, string | number>,
      fallback?: string,
   ) => string
   getFormattedJSX: (
      key: string,
      variables: Record<string, React.ReactNode>,
      fallback?: string,
   ) => React.ReactNode
   getTransformedString: (
      key: string,
      transformFn: (varName: string, value: string | number) => string,
      fallback?: string,
   ) => string
   isLoading: boolean
   language: string
   changeLanguage: (newLanguage: string) => void
   refreshStrings: () => Promise<LocalizationResult>
}

// Global cache to persist strings between component remounts during the session
const globalStringCache: StringCache = {
   data: {},
   language: 'en-US', // Default language
}

/**
 * Custom hook for localization with session-based caching
 *
 * @param hostUrl - Base URL for API calls (only used on first load or if not cached)
 * @param options - Configuration options
 * @returns Localization utilities
 */
const useLocalization = (
   hostUrl: string,
   options: LocalizationOptions = {},
): UseLocalizationReturn => {
   const { showErrors = true } = options

   // Store the hostUrl in the cache on first mount
   useEffect(() => {
      if (!globalStringCache.hostUrl) {
         globalStringCache.hostUrl = hostUrl
      }
   }, [hostUrl])

   const [strings, setStrings] = useState<Record<string, string>>(
      globalStringCache.data,
   )
   const [language, setLanguage] = useState<string>(globalStringCache.language)
   const [isLoading, setIsLoading] = useState<boolean>(false)
   const isMounted = useRef<boolean>(true)

   // Store the hostUrl in a ref to avoid dependency changes
   const hostUrlRef = useRef<string>(globalStringCache.hostUrl || hostUrl)

   // Fetch strings from the backend
   const fetchLocalizedStrings = useCallback(
      async (forceRefresh: boolean = false): Promise<LocalizationResult> => {
         // If we have strings for the current language and not forcing a refresh, use cached data
         if (
            !forceRefresh &&
            Object.keys(globalStringCache.data).length > 0 &&
            globalStringCache.language === language
         ) {
            setStrings(globalStringCache.data)
            return { status: 200, data: globalStringCache.data }
         }

         setIsLoading(true)
         try {
            // Use the cached hostUrl from the ref
            const currentHostUrl = hostUrlRef.current

            let result = await fetchStrings(currentHostUrl)

            // If error returned throw error
            if (result.status != 200) {
               const error = result.error
               throw new Error(error || 'Unable to fetch localization strings')
            }

            if (result.status === 200 && result.data) {
               // Update local state only if component is still mounted
               if (isMounted.current) {
                  setStrings(result.data[language])

                  // Update global cache for the session
                  globalStringCache.data = result.data[language]
                  globalStringCache.language = language

                  // Show error if no strings were returned
                  if (
                     showErrors &&
                     Object.keys(result.data[language]).length === 0
                  ) {
                     toast.error('No localization strings found.', {
                        duration: 5000,
                     })
                  }
               }

               setIsLoading(false)
               return { status: 200, data: result.data[language] }
            } else {
               throw new Error(
                  result.error || 'Unable to fetch localization strings',
               )
            }
         } catch (error) {
            const errorMessage =
               error instanceof Error
                  ? error.message
                  : 'Unable to fetch localization strings.'
            if (showErrors) {
               console.error(errorMessage)
               toast.error(errorMessage, { duration: 5000 })
            }
            setIsLoading(false)
            return { status: 500, error: errorMessage }
         }
      },
      [language, showErrors],
   )

   // Get a localized string by key
   const getString = useCallback(
      (key: string, fallback: string = key): string => {
         return strings[key] || fallback
      },
      [strings],
   )

   // Get a localized string with variable interpolation
   // Example: getFormattedString("Hello {name}", { name: "John" }) => "Hello John"
   const getFormattedString = useCallback(
      (
         key: string,
         variables: Record<string, string | number> = {},
         fallback: string = key,
      ): string => {
         let text = strings[key] || fallback

         // Replace {variableName} with the actual value
         Object.entries(variables).forEach(([varName, value]) => {
            text = text.replace(new RegExp(`{${varName}}`, 'g'), String(value))
         })

         return text
      },
      [strings],
   )

   // Get a localized string with variable interpolation that supports JSX elements
   // Example: getFormattedJSX("key", { link: <a href="...">click here</a> }, "Visit {link} for details") => <>Visit <a href="...">click here</a> for details</>
   const getFormattedJSX = useCallback(
      (
         key: string,
         variables: Record<string, React.ReactNode>,
         fallback: string = key,
      ): React.ReactNode => {
         const text = strings[key] || fallback

         // Split text on {varName} placeholders while capturing the variable names
         const parts = text.split(/\{([^}]+)\}/)

         // parts alternates between text segments and variable names:
         // "Hello {name}, welcome to {place}" => ["Hello ", "name", ", welcome to ", "place", ""]
         if (parts.length === 1) return text

         return (
            <>
               {parts.map(
                  (part, index) =>
                     index % 2 === 0 ? (
                        part // text segment
                     ) : variables[part] !== undefined ? (
                        <React.Fragment key={index}>
                           {variables[part]}
                        </React.Fragment>
                     ) : (
                        `{${part}}`
                     ), // unmatched variable, leave as-is
               )}
            </>
         )
      },
      [strings],
   )

   // Get a localized string with variable substitution using a transform function
   // Example: getTransformedString("price_display", (varName, value) => varName === 'price' ? `$${Number(value).toFixed(2)}` : value)
   // If the string is "Price: {price:123.45}", the result will be "Price: $123.45"
   const getTransformedString = useCallback(
      (
         key: string,
         transformFn: (varName: string, value: string | number) => string,
         fallback: string = key,
      ): string => {
         // Get the localized string or fallback
         const text = strings[key] || fallback

         // Match pattern {varName:value}
         const pattern = /{([^:}]+):([^}]*)}/g

         // Replace all matches with transformed values
         return text.replace(pattern, (match, varName, value) => {
            return transformFn(varName, value)
         })
      },
      [strings],
   )

   // Change the current language
   const changeLanguage = useCallback(
      (newLanguage: string): void => {
         if (newLanguage !== language) {
            setLanguage(newLanguage)
            // Language change will trigger a new fetch in the useEffect
         }
      },
      [language],
   )

   // Fetch strings on initial load or language change
   useEffect(() => {
      fetchLocalizedStrings()

      return () => {
         isMounted.current = false
      }
   }, [language, fetchLocalizedStrings])

   // Clean up on unmount
   useEffect(() => {
      return () => {
         isMounted.current = false
      }
   }, [])

   return {
      strings,
      getString,
      getFormattedString,
      getFormattedJSX,
      getTransformedString,
      isLoading,
      language,
      changeLanguage,
      refreshStrings: () => fetchLocalizedStrings(true),
   }
}

export default useLocalization
